import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import { isSuperAdmin, getDisplayRole } from '../../utils/roles';
import { useAuth } from '../../contexts/AuthContext';

interface SidebarProps {
  isCollapsed?: boolean;
  userRole?: 'admin' | 'member';
  isSuperAdmin?: boolean;
  activeDashboardTab?: 'group' | 'admin' | 'subscription' | 'overview' | 'contributions';
  activeReportType?: 'daily' | 'weekly' | 'monthly';
}

const Sidebar = ({
  isCollapsed = false,
  // Default to 'member' for safety: this Sidebar is sometimes rendered without props,
  // and defaulting to 'admin' would incorrectly show admin links to members.
  userRole: propUserRole = 'member',
  isSuperAdmin: propIsSuperAdmin = false,
  activeDashboardTab,
  activeReportType,
}: SidebarProps) => {
  const location = useLocation();
  const [currentHash, setCurrentHash] = useState<string>('');
  
  // Get user profile - useAuth must be called unconditionally at top level
  const authContext = useAuth();
  const userProfile = authContext?.userProfile || null;
  const effectiveRoleFromContext = authContext?.effectiveRole;
  
  // Check if user is group_admin (not super_admin)
  // Use safe navigation to prevent errors during initial load
  const userRoleFromProfile = userProfile?.role;
  const isGroupAdminUser = userRoleFromProfile === 'group_admin'; // Only group_admin, not super_admin
  const isSuperAdminUser = userRoleFromProfile ? isSuperAdmin(userRoleFromProfile) : propIsSuperAdmin;

  // Track hash changes
  useEffect(() => {
    const handleHashChange = () => {
      setCurrentHash(window.location.hash.replace('#', ''));
    };

    // Set initial hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const isActivePath = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Check if we're on dashboard page
  const isDashboard = location.pathname === '/admin-dashboard' || 
                      location.pathname === '/member-dashboard' || 
                      location.pathname === '/super-admin-dashboard';

  // Check if we're on reports page
  const isReports = location.pathname === '/reports';

  // Dashboard navigation items (shown when on dashboard)
  const dashboardSections = [
    {
      title: 'Dashboard',
      items: [
        {
          label: 'Overview',
          path: '/member-dashboard#overview',
          icon: 'LayoutDashboard',
          roles: ['member'],
          isTab: true,
        },
        {
          label: 'Contributions',
          path: '/member-dashboard#contributions',
          icon: 'DollarSign',
          roles: ['member'],
          isTab: true,
        },
        {
          label: 'Group',
          path: '/admin-dashboard#group',
          icon: 'Users',
          roles: ['admin'],
          isTab: true,
        },
        {
          label: 'Group Admin',
          path: '/admin-dashboard#admin',
          icon: 'Settings',
          roles: ['admin'],
          isTab: true,
        },
        {
          label: 'Subscription',
          path: '/admin-dashboard#subscription',
          icon: 'Package',
          roles: ['admin'],
          isTab: true,
        },
      ],
    },
    {
      title: 'Quick Links',
      items: [
        {
          label: 'Invoices',
          path: '/invoices',
          icon: 'FileText',
          roles: ['admin'],
        },
      ],
    },
  ];

  // Reports navigation items (shown when on reports page)
  const reportsSections = [
    {
      title: 'Reports',
      items: [
        {
          label: 'Daily Report',
          path: '/reports#daily',
          icon: 'Calendar',
          roles: ['admin', 'member'],
          isTab: true,
        },
        {
          label: 'Weekly Report',
          path: '/reports#weekly',
          icon: 'CalendarDays',
          roles: ['admin', 'member'],
          isTab: true,
        },
        {
          label: 'Monthly Report',
          path: '/reports#monthly',
          icon: 'CalendarRange',
          roles: ['admin', 'member'],
          isTab: true,
        },
      ],
    },
  ];

  // Management navigation items (shown when on management pages)
  const managementSections = [
    {
      title: 'Management',
      items: [
        {
          label: 'Group Management',
          path: '/group-management',
          icon: 'Users',
          roles: ['admin'],
        },
        {
          label: 'Members',
          // Super admin uses group-management hash view; group admins use a role-specific route
          path: isSuperAdminUser ? '/group-management#members' : '/group-admin/members',
          icon: 'UserPlus',
          roles: ['admin'],
        },
        {
          label: 'Subscription',
          path: '/subscription-management',
          icon: 'Package',
          roles: ['admin'],
        },
        // Package plans management is ONLY for super_admin
        ...(isSuperAdminUser ? [{
          label: 'Package Plans',
          path: '/subscription-plan-management',
          icon: 'Package',
          roles: ['admin'],
        }] : []),
        // Settings page is ONLY for super_admin
        ...(isSuperAdminUser ? [{
          label: 'Settings',
          path: '/account-settings',
          icon: 'Settings',
          roles: ['admin'],
        }] : []),
        // Approving page is ONLY for super_admin
        ...(isSuperAdminUser ? [{
          label: 'Approving',
          path: '/payment-processing',
          icon: 'CheckCircle',
          roles: ['admin'],
        }] : []),
        // Data Backup is ONLY for super_admin
        ...(isSuperAdminUser ? [{
          label: 'Data Backup',
          path: '/data-backup',
          icon: 'Database',
          roles: ['admin'],
          superAdminOnly: true,
        }] : []),
      ],
    },
    {
      title: 'Analytics & Reports',
      items: [
        {
          label: 'Advanced Analytics',
          path: '/advanced-analytics',
          icon: 'BarChart3',
          roles: ['admin'],
        },
        {
          label: 'Reports',
          path: '/reports',
          icon: 'FileText',
          roles: ['admin', 'member'],
        },
        {
          label: 'Platform Analytics',
          path: '/platform-analytics',
          icon: 'TrendingUp',
          roles: ['admin'],
          superAdminOnly: true,
        },
      ],
    },
    {
      title: 'Financial Services',
      items: [
        {
          label: 'Loan Requests',
          path: '/loan-request',
          icon: 'CreditCard',
          roles: ['admin', 'member'],
        },
        {
          label: 'Loan Management',
          path: '/loan-management',
          icon: 'Wallet',
          roles: ['admin', 'member'],
        },
        {
          label: 'Contributions',
          path: '/contribution-recording',
          icon: 'DollarSign',
          roles: ['admin'],
        },
        {
          label: 'Payments',
          path: '/payment-status',
          icon: 'Wallet',
          roles: ['admin', 'member'],
        },
        {
          label: 'Invoices',
          path: '/invoices',
          icon: 'FileText',
          roles: ['admin'],
        },
      ],
    },
    {
      title: 'Learning & Support',
      items: [
        {
          label: 'Notifications',
          path: '/notifications',
          icon: 'Bell',
          roles: ['admin', 'member'],
        },
        {
          label: 'Profile',
          path: '/member-profile',
          icon: 'User',
          roles: ['admin', 'member'],
        },
      ],
    },
  ];

  // Use appropriate sections based on current page
  let navigationSections;
  if (isDashboard) {
    navigationSections = dashboardSections;
  } else if (isReports) {
    navigationSections = reportsSections;
  } else {
    navigationSections = managementSections;
  }

  // Derive role from the authenticated profile when available.
  // Prefer AuthContext's effectiveRole (includes group_members.role based admin access), fallback to platform role.
  const effectiveRole =
    effectiveRoleFromContext ??
    (userRoleFromProfile ? getDisplayRole(userRoleFromProfile, (userProfile as any)?.group_roles) : propUserRole);
  
  const filteredSections = navigationSections.map(section => ({
    ...section,
    items: section.items.filter((item: any) => {
      if (!item.roles.includes(effectiveRole)) return false;
      if (item.superAdminOnly && !isSuperAdminUser) return false;
      return true;
    }),
  })).filter(section => section.items.length > 0);

  return (
    <aside
      className={`
        fixed top-20 left-0 bottom-0 border-r border-border
        transition-all duration-300 z-40
        hidden lg:block
        ${isCollapsed ? 'w-16' : 'w-64'}
        ${isGroupAdminUser && !isSuperAdminUser 
          ? 'bg-gradient-to-br from-blue-50 via-white to-emerald-50' 
          : 'bg-card'
        }
      `}
    >
      <nav className="h-full overflow-y-auto py-6">
        {filteredSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-6">
            {!isCollapsed && (
              <h3 className="px-6 mb-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {section.title}
              </h3>
            )}
            <div className="space-y-1 px-3">
              {section.items.map((item: any) => {
                // Handle tab navigation for dashboard
                if (item.isTab && isDashboard) {
                  const tabName = item.path.split('#')[1] as 'group' | 'admin' | 'subscription' | 'overview' | 'contributions';
                  const isActive = activeDashboardTab === tabName;
                  
                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        // Trigger tab change via custom event
                        window.dispatchEvent(new CustomEvent('dashboard-tab-change', { detail: { tab: tabName } }));
                      }}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2.5 rounded-sm
                        transition-interactive hover-scale
                        ${isActive
                          ? 'bg-primary text-primary-foreground shadow-elevation-1'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }
                      `}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <Icon name={item.icon} size={20} />
                      {!isCollapsed && (
                        <span className="text-sm font-medium">{item.label}</span>
                      )}
                    </button>
                  );
                }

                // Handle tab navigation for reports
                if (item.isTab && isReports) {
                  const reportType = item.path.split('#')[1] as 'daily' | 'weekly' | 'monthly';
                  const isActive = activeReportType === reportType || (!activeReportType && reportType === 'daily');
                  
                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        // Change report type via custom event
                        window.dispatchEvent(new CustomEvent('report-type-change', { detail: { type: reportType } }));
                        // Update URL hash
                        window.location.hash = reportType;
                      }}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2.5 rounded-sm
                        transition-interactive hover-scale
                        ${isActive
                          ? 'bg-primary text-primary-foreground shadow-elevation-1'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }
                      `}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <Icon name={item.icon} size={20} />
                      {!isCollapsed && (
                        <span className="text-sm font-medium">{item.label}</span>
                      )}
                    </button>
                  );
                }

                // Regular link navigation
                // Special handling for Group Management to clear hash
                if (item.path === '/group-management') {
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => {
                        window.location.hash = '';
                        setCurrentHash('');
                      }}
                      className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-sm
                        transition-interactive hover-scale
                        ${isActivePath(item.path) && !currentHash
                          ? 'bg-primary text-primary-foreground shadow-elevation-1'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }
                      `}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <Icon name={item.icon} size={20} />
                      {!isCollapsed && (
                        <span className="text-sm font-medium">{item.label}</span>
                      )}
                    </Link>
                  );
                }

                // Special handling for Members on Group Management page:
                // when already on /group-management, force hash change and scroll.
                if (
                  (item.path === '/group-management#members' || item.path === '/group-admin/members') &&
                  location.pathname === '/group-management'
                ) {
                  const isActive = currentHash === 'members';

                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        // Update hash so GroupManagement can react
                        window.location.hash = 'members';
                        setCurrentHash('members');
                        // Optional: scroll main content to top
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2.5 rounded-sm
                        transition-interactive hover-scale
                        ${
                          isActive
                            ? 'bg-primary text-primary-foreground shadow-elevation-1'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }
                      `}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <Icon name={item.icon} size={20} />
                      {!isCollapsed && (
                        <span className="text-sm font-medium">{item.label}</span>
                      )}
                    </button>
                  );
                }

                // Regular link navigation (including hash-based links like Members from other pages)
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-sm
                      transition-interactive hover-scale
                      ${
                        // If link has hash (e.g. /group-management#members), use hash + path to determine active
                        item.path.includes('#')
                          ? (() => {
                              const [basePath, hash] = item.path.split('#');
                              const isActive =
                                isActivePath(basePath) && currentHash === hash;
                              return isActive
                                ? 'bg-primary text-primary-foreground shadow-elevation-1'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted';
                            })()
                          : item.path === '/group-admin/members'
                          ? (() => {
                              const isActive =
                                (location.pathname === '/group-management' && currentHash === 'members') ||
                                location.pathname === '/group-admin/members';
                              return isActive
                                ? 'bg-primary text-primary-foreground shadow-elevation-1'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted';
                            })()
                          : isActivePath(item.path)
                          ? 'bg-primary text-primary-foreground shadow-elevation-1'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }
                    `}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <Icon name={item.icon} size={20} />
                    {!isCollapsed && (
                      <span className="text-sm font-medium">{item.label}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;